import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";
import {
  useTenant,
  type FeatureNavNode,
  type StoredAuthUser,
} from "@nasam/core";
import { Avatar, AvatarFallback, AvatarImage, cn } from "@nasam/ui";
import { resolveIcon } from "./iconRegistry";
import { navNodeTitle } from "./navNodeTitle";

// Plain unmount, not a Radix primitive, so there's no Presence to hold the
// node mounted through animate-out — match it to the exit animation's
// duration (motion.css's --motion-duration-fast) by hand.
const CLOSE_TRANSITION_MS = 140;

export interface NavDrawerProps {
  readonly isOpen: boolean;
  readonly activeRailNode: FeatureNavNode | undefined;
  readonly activeNavNode: FeatureNavNode | undefined;
  readonly onNavigate: (path: string) => void;
  readonly user?: StoredAuthUser | null | undefined;
  readonly language?: string | undefined;
}

function isGroupNode(node: FeatureNavNode): boolean {
  return node.path === undefined && (node.children?.length ?? 0) > 0;
}

function containsNode(node: FeatureNavNode, targetId: string | undefined) {
  if (targetId === undefined) return false;
  for (const child of node.children ?? []) {
    if (child.id === targetId || containsNode(child, targetId)) return true;
  }
  return false;
}

/**
 * The chain of group ids (one per depth) that has to be open for `targetId`
 * to be visible, or `null` if it isn't reachable from `nodes` at all.
 */
function activeGroupPath(
  nodes: readonly FeatureNavNode[],
  targetId: string | undefined,
): readonly string[] | null {
  if (targetId === undefined) return null;
  for (const node of nodes) {
    if (node.id === targetId) return [];
    if (isGroupNode(node)) {
      const nested = activeGroupPath(node.children ?? [], targetId);
      if (nested !== null) return [node.id, ...nested];
    }
  }
  return null;
}

export function NavDrawer({
  isOpen,
  activeRailNode,
  activeNavNode,
  onNavigate,
  user,
  language,
}: NavDrawerProps) {
  const { t } = useTranslation();
  const { branding } = useTenant();
  const drawerNodes = useMemo(
    () => activeRailNode?.children ?? [],
    [activeRailNode],
  );

  // Single-open accordion, one entry per depth: expandedPath[0] is the open
  // top-level group's id, expandedPath[1] the open id among *its* children,
  // and so on. At most one panel per level is ever open, which is what keeps
  // the rest of the list from being pushed out of view (see the inner-scroll
  // note on the expanded-children wrapper below).
  const [expandedPath, setExpandedPath] = useState<readonly string[]>([]);

  // Re-seed the open path whenever real navigation happens (activeNavNode
  // changes) or the drawer starts showing a different rail group's tabs
  // (activeRailNode changes) -- but not on every render, so a plain click
  // that only toggles a panel (no navigation) isn't immediately fought here.
  useEffect(() => {
    setExpandedPath(activeGroupPath(drawerNodes, activeNavNode?.id) ?? []);
  }, [activeRailNode?.id, activeNavNode?.id, drawerNodes]);

  function toggleGroup(depth: number, nodeId: string) {
    setExpandedPath((current) =>
      current[depth] === nodeId
        ? current.slice(0, depth)
        : [...current.slice(0, depth), nodeId],
    );
  }

  const hasContent = drawerNodes.length > 0 && activeRailNode !== undefined;
  const [mounted, setMounted] = useState(isOpen && hasContent);

  useEffect(() => {
    if (isOpen && hasContent) {
      setMounted(true);
      return;
    }
    if (!mounted) return;
    const timeout = setTimeout(() => setMounted(false), CLOSE_TRANSITION_MS);
    return () => clearTimeout(timeout);
  }, [isOpen, hasContent, mounted]);

  if (!mounted) {
    return null;
  }

  const titleOf = (node: FeatureNavNode) => navNodeTitle(node, t, user);

  const renderLeaf = (node: FeatureNavNode, nested: boolean) => {
    const isActive = activeNavNode?.id === node.id;
    const NodeOverride = node.component;

    if (NodeOverride !== undefined) {
      return <NodeOverride key={node.id} node={node} isActive={isActive} />;
    }

    const Icon = node.icon !== undefined ? resolveIcon(node.icon) : null;

    return (
      <button
        key={node.id}
        type="button"
        data-testid={`drawer-item-${node.id}`}
        data-active={isActive ? "true" : "false"}
        disabled={node.disabled === true}
        onClick={() => {
          if (node.path !== undefined) {
            onNavigate(node.path);
          }
        }}
        className={cn(
          "flex w-full shrink-0 items-center gap-1 rounded-md text-start transition-colors duration-150 ease-emphasized focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary",
          nested
            ? "min-h-9 px-2 py-2 text-body-m-r"
            : "h-11 px-3 py-2 text-body-l-r",
          isActive
            ? "bg-primary-button-background text-primary-button-text"
            : "text-secondary-button-text hover:bg-container-hover",
          node.disabled === true && "pointer-events-none opacity-50",
        )}
      >
        {Icon !== null && (
          <Icon className={cn("shrink-0", nested ? "size-6" : "size-4")} />
        )}
        <span className="truncate px-1">{titleOf(node)}</span>
      </button>
    );
  };

  const renderGroup = (node: FeatureNavNode, depth: number) => {
    const children = node.children ?? [];
    const holdsActive = containsNode(node, activeNavNode?.id);
    const expanded = expandedPath[depth] === node.id;
    const Icon = node.icon !== undefined ? resolveIcon(node.icon) : null;

    return (
      <div key={node.id} className="w-full shrink-0 rounded-md">
        <button
          type="button"
          data-testid={`drawer-group-${node.id}`}
          aria-expanded={expanded}
          onClick={() => toggleGroup(depth, node.id)}
          className={cn(
            "flex h-11 w-full shrink-0 items-center justify-between rounded-md px-3 py-2 text-secondary-button-text transition-colors duration-150 ease-emphasized hover:bg-container-hover focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary",
            // The panel is collapsed but one of its descendants is the active
            // route -- a light-grey tint marks "something in here is open
            // elsewhere," distinct from both the active leaf's own highlight
            // and the plain hover tint.
            !expanded && holdsActive && "bg-neutral-100",
          )}
        >
          <span className="flex items-center gap-1">
            {Icon !== null && <Icon className="size-4 shrink-0" />}
            <span className="truncate px-1 text-body-l-r">{titleOf(node)}</span>
          </span>
          <ChevronDown
            className={cn(
              "size-4 shrink-0 text-secondary-icon transition-transform duration-150 ease-emphasized",
              expanded && "rotate-180",
            )}
          />
        </button>
        {expanded && (
          // Always sized to its full content -- no internal scroll here.
          // When there isn't room, the whole `nav` list below scrolls as one
          // unit instead (see its scrollbar-thin/overflow-y-auto), so a
          // short panel never gets stunted to a couple of visible rows.
          <div className="flex flex-col gap-2 rounded-md pb-2 pe-2 ps-4 animate-in fade-in-0 slide-in-from-top-1">
            {children.map((child) =>
              isGroupNode(child)
                ? renderGroup(child, depth + 1)
                : renderLeaf(child, true),
            )}
          </div>
        )}
      </div>
    );
  };

  const displayName =
    (language === "ar" ? user?.nameAr : user?.nameEn) ??
    user?.nameEn ??
    user?.username ??
    "";
  const initials = displayName.trim().slice(0, 2).toUpperCase();

  // The grid track (AppShell) animates 15rem -> 0fr on close; this box has no
  // intrinsic width so it shrinks with the track, and clips the fixed w-60
  // aside instead of letting it squish or spill onto main.
  return (
    <div className="hidden md:block h-full overflow-hidden">
      <aside
        aria-label="Navigation drawer"
        data-testid="nav-drawer"
        data-node-id="4980:70403"
        className={cn(
          "relative z-20 flex h-full w-60 shrink-0 flex-col justify-between rounded-s-xl border-e border-container-default-border bg-container-default-background pt-4 select-none",
          isOpen
            ? "animate-in fade-in-0 slide-in-from-start-4"
            : "pointer-events-none animate-out fade-out-0 slide-out-to-start-4",
        )}
      >
        <div className="flex min-h-0 flex-1 flex-col gap-6">
          <div className="flex h-[2.8125rem] shrink-0 items-center gap-2 px-2">
            <img
              src={branding?.logoUrl ?? "/brand/logo-mark.png"}
              alt=""
              className="size-14 shrink-0 object-contain"
            />
            {/* A tenant's logo already carries its own wordmark; only the
                default mark needs this text next to it. */}
            {branding?.logoUrl === undefined && (
              <span className="bg-gradient-to-r from-blue-purple-700 via-blue-purple-500 to-blue-purple-900 bg-clip-text text-body-xl-b-ar text-transparent">
                NASAM
              </span>
            )}
          </div>

          {/* The whole list scrolls as one unit -- logo above and the user
              footer below stay pinned outside `nav`. Every row (collapsed or
              expanded) is shrink-0/sizes to its content, so this only grows
              a scrollbar when the drawer genuinely doesn't have room, rather
              than stunting an open panel to a couple of visible rows. */}
          <nav
            aria-label="Secondary navigation"
            className="scrollbar-thin flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-2"
          >
            {drawerNodes.map((node) =>
              isGroupNode(node) ? renderGroup(node, 0) : renderLeaf(node, false),
            )}
          </nav>
        </div>

        <div className="flex h-[3.875rem] shrink-0 items-center gap-[0.5625rem] border-t border-neutral-200 px-2">
          <Avatar className="size-10 shrink-0">
            {user?.avatarUrl ? (
              <AvatarImage
                src={user.avatarUrl}
                alt=""
                className="object-cover"
              />
            ) : null}
            <AvatarFallback className="text-body-s-b">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-body-s-r text-purple-900">
              {displayName}
            </span>
            <span className="truncate text-caption-s text-tab-normal-text">
              {user?.email ?? ""}
            </span>
          </div>
        </div>
      </aside>
    </div>
  );
}
